import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { Box, Typography } from '@mui/material';
import { useAccountBookStore } from '@/store/accountBookStore';
import SectionHeader from '../SectionHeader';
import { generateCategoryColors } from '@/constants/chartPalette';

/** 순위 리스트에 개별 표시할 상위 카테고리 수 — 나머지는 '그 외'로 묶음 */
const TOP_N = 6;

const CategoryChart: React.FC = () => {
  const {
    dashboardData,
    isLoading
  } = useAccountBookStore();

  // 데이터 로딩은 Dashboard 페이지/store 초기화 흐름이 담당 — 여기서는 표시만 한다

  // 카테고리별 색상 동적 생성
  const categoryColors = generateCategoryColors(
    dashboardData.categoryExpenses.map(item => item.category)
  );

  // 카테고리별 지출 데이터를 차트 형식으로 변환
  const data = dashboardData.categoryExpenses.map((item) => ({
    name: item.category,
    value: item.amount,
    percentage: item.percentage,
    color: categoryColors[item.category],
  }));
  
  const COLORS = data.map(item => item.color);

  const formatCurrency = (value: number) => value.toLocaleString('ko-KR');

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: 'var(--bg-base)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--r-12)',
            p: 2,
            boxShadow: 'var(--shadow-3)',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {data.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            금액: {formatCurrency(data.value)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            비율: {data.percentage}%
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const total = data.reduce((sum, item) => sum + item.value, 0);
  const topItems = data.slice(0, TOP_N);
  const restItems = data.slice(TOP_N);
  const restValue = restItems.reduce((sum, item) => sum + item.value, 0);
  const restPercentage = total > 0 ? Math.round((restValue / total) * 100) : 0;

  /** 순위 리스트 한 줄 — 색 점 · 카테고리명 · 금액 · 비율 */
  const LegendRow: React.FC<{
    color: string;
    name: string;
    value: number;
    percentage: number;
  }> = ({ color, name, value, percentage }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: color,
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontSize: 14,
          color: 'var(--fg-secondary)',
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          letterSpacing: '0.014em',
        }}
      >
        {name}
      </Typography>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 600,
          fontFamily: 'var(--font-numeric)',
          fontVariantNumeric: 'tabular-nums',
          color: 'var(--fg-strong)',
        }}
      >
        {formatCurrency(value)}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          color: 'var(--fg-tertiary)',
          width: 44,
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {percentage}%
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <SectionHeader
        eyebrow="이번 연도"
        title="카테고리별 지출"
        subtitle="지출 비중을 한눈에"
      />

      {isLoading ? (
        <Box sx={{ 
          height: 300, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: 'text.secondary'
        }}>
          <Typography>데이터를 불러오는 중...</Typography>
        </Box>
      ) : data.length === 0 ? (
        <Box sx={{ 
          height: 300, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: 'text.secondary'
        }}>
          <Typography>{dashboardData.selectedYear}년 지출 데이터가 없습니다.</Typography>
        </Box>
      ) : (
        <>
          {/* 도넛 + 중앙 합계 — 합계는 중앙에, 세부는 아래 순위 리스트로 */}
          <Box sx={{ position: 'relative', flex: 1, minHeight: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius="95%"
                  innerRadius="68%"
                  paddingAngle={2}
                  cornerRadius={6}
                  dataKey="value"
                >
                  {data.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <Typography
                sx={{ fontSize: 13, color: 'var(--fg-tertiary)', letterSpacing: '0.014em' }}
              >
                총 지출
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'var(--font-numeric)',
                  fontVariantNumeric: 'tabular-nums',
                  fontSize: 22,
                  fontWeight: 800,
                  color: 'var(--fg-strong)',
                  letterSpacing: '-0.012em',
                }}
              >
                {formatCurrency(total)}
              </Typography>
            </Box>
          </Box>

          {/* 상위 카테고리 순위 리스트 — 금액·비율 함께 표시 */}
          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
              maxHeight: 176,
              overflowY: 'auto',
              pr: 0.5,
              flexShrink: 0,
            }}
          >
            {topItems.map((item) => (
              <LegendRow
                key={item.name}
                color={item.color}
                name={item.name}
                value={item.value}
                percentage={item.percentage}
              />
            ))}
            {restItems.length > 0 && (
              <LegendRow
                color="var(--c-cool-300)"
                name={`그 외 ${restItems.length}개`}
                value={restValue}
                percentage={restPercentage}
              />
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default CategoryChart;
